import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { submitAccessRequest } from '../api/requests';

export function RequestAccessView() {
  const { userAttributes, getJwtToken } = useAuth();

  const email = userAttributes?.email ?? '';
  const defaultName = [userAttributes?.given_name, userAttributes?.family_name]
    .filter(Boolean)
    .join(' ');

  const [name, setName] = useState(defaultName);
  const [reason, setReason] = useState('');
  const [requestedRole, setRequestedRole] = useState<'user' | 'admin'>('user');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !reason.trim()) {
      setError('Name and justification are required.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getJwtToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }
      await submitAccessRequest(
        { name: name.trim(), reason: reason.trim(), requestedRole },
        token,
      );
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit request';
      if (msg.includes('409')) {
        setError('You already have a pending access request.');
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div>
        <div className="view__header">
          <h1 className="view__title">Request Access</h1>
        </div>
        <div className="settings-card">
          <div className="request-access__success">
            <h2>Request Submitted</h2>
            <p>
              Your access request has been submitted for review.
              An administrator will review your request and you will be granted
              access once approved.
            </p>
            <p className="request-access__email">
              Submitted as: <strong>{email}</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Request Access</h1>
      </div>

      <p className="view__description">
        Submit a request for access to the Flux LLM Gateway.
        Your request will be reviewed by an administrator.
      </p>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      <div className="settings-card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-group__label" htmlFor="request-email">
              Email
            </label>
            <input
              id="request-email"
              className="form-group__input"
              type="email"
              value={email}
              disabled
            />
            <span className="form-group__hint">
              This is your authenticated email address.
            </span>
          </div>

          <div className="form-group">
            <label className="form-group__label" htmlFor="request-name">
              Full Name
            </label>
            <input
              id="request-name"
              className="form-group__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-group__label" htmlFor="request-role">
              Requested Role
            </label>
            <select
              id="request-role"
              className="form-group__input"
              value={requestedRole}
              onChange={(e) => setRequestedRole(e.target.value as 'user' | 'admin')}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <span className="form-group__hint">
              Select "User" for standard API access or "Admin" for administrative privileges.
            </span>
          </div>

          <div className="form-group">
            <label className="form-group__label" htmlFor="request-reason">
              Justification
            </label>
            <textarea
              id="request-reason"
              className="form-group__input"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe why you need access and what you plan to use it for..."
              required
            />
          </div>

          <div className="modal__actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
